import clsx from 'clsx';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { LicenseName, LicensesType, TLicensePrice } from '@/core/infra';
import { formatPrice } from '@/helper';
import Icon from '../common/Icon';
import Spinner from '../common/Spinner';
import { PersonalLicenseDetails } from '@/components/PersonalLicenses/PersonalLicenseDetails';
import { CheckoutPanel } from './CheckoutPanel';
import { getDiscount } from '@/helper';
import { useData } from './hooks/useData';
import { useContextState } from '@/components/context';
import Warning from '../../public/img/checkout/warning.svg';
import FullIcon from '../../public/img/license/full-icon.svg';
import PlayerIcon from '../../public/img/license/player-icon.svg';
import LicenseFeature from '../../public/img/license/license-feature.svg';
import s from './ConfirmBuyingLicense.module.scss';

const FEATURES = {
  [LicensesType.STOCK_MARKET]: [1, 2, 3, 4],
  [LicensesType.PLAYER]: [3, 5],
};

export const ConfirmBuyingLicenseComponent = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { authorized } = useContextState();
  const { prices, license, loading, setLoading, licenseType, paymentMethods, sameTypeLicenseId } =
    useData(router?.query || {});

  const { priceId, licenseId: extendLicenseId } = router.query;

  const [selectedPrice, setSelectedPrice] = useState<TLicensePrice>();

  useEffect(() => {
    if (prices) {
      const price = prices.find((price) => price.priceId === priceId) || prices[0];
      setSelectedPrice(price);
    }
  }, [prices]);

  const heading = useMemo(() => {
    if (!license) return t('license_buy-a-full-license');
    if (license.licenseStatus !== 'EXPIRED') return t('license_extend-license');
    return t('license_renew-license');
  }, [license]);

  return (
    <div className="content">
      <div className={s.root}>
        <div>
          <button className={clsx('btn', s.back)} onClick={router.back}>
            <Icon name="20-arrow-left" file="sprite" width="6" height="11" />
          </button>
          <h1 className={s.heading}>{heading}</h1>
          {extendLicenseId && (
            <div className={s.subtitle}>
              {t('license_extend-subtitle')}&nbsp;
              <Link href={'/terminal/prices'} passHref>
                <a className={s.pricesLink}>{t('license_to-prices-link')}</a>
              </Link>
            </div>
          )}
          <div className={s.typeWrap}>
            <div className={s.typeIcon}>
              {licenseType === LicensesType.STOCK_MARKET ? <FullIcon /> : <PlayerIcon />}
            </div>
            <div>
              <h2 className={s.licenseName}>
                {LicenseName[licenseType]} {t('license_name-end')}
              </h2>
              {extendLicenseId && license ? (
                <PersonalLicenseDetails license={license} />
              ) : (
                <div className={s.new}>{t('license_new-license')}</div>
              )}
            </div>
            {sameTypeLicenseId && (
              <div className={s.existsSameType}>
                <div className={s.existsSameTypeIcon}>
                  <Warning />
                </div>
                <div>{t('license_new-type-exists')}</div>
                <Link
                  href={{
                    pathname: `/terminal/confirm-buying-license/${licenseType.toLowerCase()}`,
                    query: { licenseId: sameTypeLicenseId, priceId: selectedPrice?.priceId },
                  }}
                >
                  <a className={s.existsSameTypeBtn}>{t('license_new-type-exists-renew')}</a>
                </Link>
              </div>
            )}
          </div>
          <div className={s.periods}>
            <div>{t('license_license-period')}</div>
            <div className={clsx(s.periodList, { [s.single]: prices.length === 1 })}>
              {prices.map((price) => (
                <button
                  key={price.priceId}
                  className={clsx(s.period, {
                    [s.active]: selectedPrice?.priceId === price.priceId,
                  })}
                  onClick={setSelectedPrice.bind(this, price)}
                >
                  <div>{t(`prices_subscription_${price.name}`)}</div>
                  <div className={s.amount}>{formatPrice(price.value, price.currency)}</div>
                  {price.discount > 0 && (
                    <div className={s.saving}>
                      {t('license_license-saving')}&nbsp;
                      {formatPrice(price.discount, price.currency)}&nbsp; (
                      {getDiscount(price.value, price.discount).save}%)
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className={s.features}>
            {FEATURES[licenseType]?.map((feature) => (
              <div className={s.feature} key={feature}>
                <div className={s.featureIcon}>
                  <LicenseFeature />
                </div>
                <div>{t(`license-features${feature}`)}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          {selectedPrice && (
            <CheckoutPanel
              type={licenseType}
              price={selectedPrice}
              license={license}
              paymentMethods={paymentMethods}
              setLoading={setLoading}
            />
          )}
        </div>
      </div>

      {loading || !authorized ? (
        <div className={s.loader}>
          <Spinner />
        </div>
      ) : null}
    </div>
  );
};
